// Settings — 个人设置
//
// 4 个区块:
//   1. 基本信息(只读): username / email / status / created_at
//   2. 改 display_name(可改 / 可清空)
//   3. 改密码(需要 old_password)
//   4. 危险区(退出登录)
//
// 由 antd 迁移至 Fluent UI v9,业务逻辑保持不变。
// 消息反馈:使用全局 toast(@/lib/toast)。
import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  makeStyles,
  tokens,
  Card,
  CardHeader,
  Input,
  Button,
  Label,
  Text,
  Title3,
  Divider,
  Avatar,
  Badge,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Spinner,
} from '@fluentui/react-components';
import {
  PersonRegular,
  LockClosedRegular,
  SaveRegular,
  ArrowClockwiseRegular,
  SignOutRegular,
  ImageAddRegular,
} from '@fluentui/react-icons';
import { useAuthStore } from '@/store/auth';
import { authApi } from '@/api/auth';
import { explainError } from '@/api/client';
import { DangerButton } from '@/components/DangerButton';
import { HelpPopover } from '@/components/HelpPopover';
import { toast } from '@/lib/toast';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    maxWidth: '720px',
  },
  card: {
    width: '100%',
    marginBottom: '20px',
  },
  cardHeader: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
  },
  hint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: '0',
    marginBottom: '12px',
  },
  // ── 描述网格(替代 Descriptions)──
  descGrid: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  descRow: {
    display: 'flex',
    alignItems: 'stretch',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':last-child': { borderBottom: 'none' },
  },
  descLabel: {
    width: '140px',
    flexShrink: 0,
    padding: '10px 16px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    alignItems: 'center',
  },
  descValue: {
    flex: 1,
    padding: '10px 16px',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  // ── 表单字段 ──
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '16px',
  },
  fieldRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  fieldHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  fieldError: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorStatusDangerForeground3,
  },
  footerText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  notLoggedIn: {
    maxWidth: '720px',
  },
});

export function SettingsPage() {
  const styles = useStyles();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useAuthStore((s) => s.logout);
  const qc = useQueryClient();

  const [logoutOpen, setLogoutOpen] = useState(false);

  // 头像上传
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // 显示名表单
  const initialDisplayName = user?.display_name ?? '';
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [dnError, setDnError] = useState('');
  const [dnSubmitting, setDnSubmitting] = useState(false);

  // 密码表单
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwErrors, setPwErrors] = useState<{
    old?: string;
    new?: string;
    confirm?: string;
  }>({});
  const [pwSubmitting, setPwSubmitting] = useState(false);

  if (!user) {
    return (
      <div className={styles.notLoggedIn}>
        <MessageBar intent="warning">
          <MessageBarBody>请先登录后再访问个人设置</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  async function onUploadAvatar(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // 前端校验:类型 + 大小
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('仅支持 PNG / JPEG / GIF / WebP 格式');
      e.target.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('头像文件不能超过 2MB');
      e.target.value = '';
      return;
    }

    setAvatarUploading(true);
    try {
      const { user: updated } = await authApi.uploadAvatar(file);
      setUser(updated);
      qc.invalidateQueries();
      toast.success('头像已更新');
    } catch (err) {
      toast.error(explainError(err));
    } finally {
      setAvatarUploading(false);
      e.target.value = '';
    }
  }

  async function onUpdateDisplayName() {
    if (displayName.length > 80) {
      setDnError('显示名最多 80 字符');
      return;
    }
    setDnError('');
    setDnSubmitting(true);
    try {
      const next = displayName.trim();
      const payload: { display_name: string | null } =
        next === '' ? { display_name: null } : { display_name: next };
      const { user: updated } = await authApi.updateMe(payload);
      setUser(updated);
      qc.invalidateQueries();
      toast.success('显示名已更新');
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setDnSubmitting(false);
    }
  }

  async function onChangePassword() {
    const errs: { old?: string; new?: string; confirm?: string } = {};
    if (!oldPwd) errs.old = '请输入当前密码';
    if (!newPwd) errs.new = '请输入新密码';
    else if (newPwd.length < 8) errs.new = '密码至少 8 字符';
    else if (newPwd.length > 1024) errs.new = '密码最多 1024 字符';
    if (!confirmPwd) errs.confirm = '请再次输入新密码';
    else if (newPwd !== confirmPwd) errs.confirm = '两次输入的密码不一致';
    setPwErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setPwSubmitting(true);
    try {
      await authApi.changePassword({
        old_password: oldPwd,
        new_password: newPwd,
      });
      toast.success('密码已更新,请使用新密码重新登录');
      setOldPwd('');
      setNewPwd('');
      setConfirmPwd('');
      setTimeout(async () => {
        await logout();
      }, 1500);
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setPwSubmitting(false);
    }
  }

  async function doLogout() {
    setLogoutOpen(false);
    await logout();
    toast.success('已退出登录');
  }

  return (
    <div className={styles.container}>
      <Title3 as="h1">个人设置</Title3>

      {/* 1. 基本信息 */}
      <Card className={styles.card} appearance="outline">
        <CardHeader header={<Text className={styles.cardHeader}>基本信息</Text>} />
        <div className={styles.descGrid}>
          <div className={styles.descRow}>
            <div className={styles.descLabel}>头像</div>
            <div className={styles.descValue}>
              {avatarUploading ? (
                <Spinner size="tiny" />
              ) : (
                <Avatar
                  size={48}
                  name={user.username}
                  image={user.avatar_url ? { src: user.avatar_url } : undefined}
                />
              )}
              {/* 仅本地账号(auth_provider = null)允许上传头像 */}
              {!user.auth_provider && (
                <>
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<ImageAddRegular />}
                    disabled={avatarUploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    更换头像
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    style={{ display: 'none' }}
                    onChange={onUploadAvatar}
                  />
                </>
              )}
            </div>
          </div>
          <div className={styles.descRow}>
            <div className={styles.descLabel}>用户名</div>
            <div className={styles.descValue}>
              <PersonRegular />
              {user.username}
            </div>
          </div>
          <div className={styles.descRow}>
            <div className={styles.descLabel}>邮箱</div>
            <div className={styles.descValue}>{user.email}</div>
          </div>
          <div className={styles.descRow}>
            <div className={styles.descLabel}>状态</div>
            <div className={styles.descValue}>
              <Badge
                appearance="filled"
                color={user.status === 'active' ? 'success' : 'warning'}
              >
                {user.status}
              </Badge>
            </div>
          </div>
          <div className={styles.descRow}>
            <div className={styles.descLabel}>注册时间</div>
            <div className={styles.descValue}>
              {new Date(user.created_at).toLocaleString('zh-CN')}
            </div>
          </div>
        </div>
      </Card>

      {/* 2. 改 display_name */}
      <Card className={styles.card} appearance="outline">
        <CardHeader header={<Text className={styles.cardHeader}>显示名</Text>} />
        <Text className={styles.hint}>
          显示名会出现在仓库列表 / commit 头像旁边等位置。留空表示使用用户名。
        </Text>
        <div className={styles.field}>
          <Label className={styles.fieldLabel} htmlFor="settings-display-name">
            显示名 <HelpPopover content="展示名称,出现在仓库列表与提交头像旁等位置。留空则使用用户名,最多 80 字符。" />
          </Label>
          <Input
            id="settings-display-name"
            placeholder="例如:张三"
            value={displayName}
            onChange={(_, d) => {
              setDisplayName(d.value);
              setDnError('');
            }}
            contentAfter={
              <Button
                appearance="transparent"
                size="small"
                icon={<ArrowClockwiseRegular />}
                title="清空"
                onClick={() => setDisplayName('')}
              />
            }
          />
          <Text className={styles.fieldHint}>1-80 字符,留空则清除</Text>
          {dnError && <Text className={styles.fieldError}>{dnError}</Text>}
        </div>
        <div className={styles.fieldRow}>
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            disabled={dnSubmitting}
            onClick={onUpdateDisplayName}
          >
            {dnSubmitting ? '保存中…' : '保存'}
          </Button>
          <Button
            appearance="subtle"
            icon={<ArrowClockwiseRegular />}
            onClick={() => {
              setDisplayName(initialDisplayName);
              setDnError('');
            }}
          >
            重置
          </Button>
        </div>
      </Card>

      {/* 3. 改密码 */}
      <Card className={styles.card} appearance="outline">
        <CardHeader header={<Text className={styles.cardHeader}>修改密码</Text>} />
        <div className={styles.field}>
          <Label className={styles.fieldLabel} htmlFor="settings-old-pwd">
            当前密码 <HelpPopover content="用于身份验证,确认是本人操作。输入你现在的登录密码。" />
          </Label>
          <Input
            id="settings-old-pwd"
            type="password"
            contentBefore={<LockClosedRegular />}
            placeholder="••••••"
            value={oldPwd}
            onChange={(_, d) => {
              setOldPwd(d.value);
              setPwErrors((p) => ({ ...p, old: undefined }));
            }}
          />
          {pwErrors.old && <Text className={styles.fieldError}>{pwErrors.old}</Text>}
        </div>
        <div className={styles.field}>
          <Label className={styles.fieldLabel} htmlFor="settings-new-pwd">
            新密码 <HelpPopover content="8–1024 位。修改成功后需使用新密码重新登录。" />
          </Label>
          <Input
            id="settings-new-pwd"
            type="password"
            contentBefore={<LockClosedRegular />}
            placeholder="新密码 (≥8 字符)"
            value={newPwd}
            onChange={(_, d) => {
              setNewPwd(d.value);
              setPwErrors((p) => ({ ...p, new: undefined }));
            }}
          />
          {pwErrors.new && <Text className={styles.fieldError}>{pwErrors.new}</Text>}
        </div>
        <div className={styles.field}>
          <Label className={styles.fieldLabel} htmlFor="settings-confirm-pwd">
            确认新密码 <HelpPopover content="再次输入新密码,两次输入必须完全一致。" />
          </Label>
          <Input
            id="settings-confirm-pwd"
            type="password"
            contentBefore={<LockClosedRegular />}
            placeholder="再次输入新密码"
            value={confirmPwd}
            onChange={(_, d) => {
              setConfirmPwd(d.value);
              setPwErrors((p) => ({ ...p, confirm: undefined }));
            }}
          />
          {pwErrors.confirm && (
            <Text className={styles.fieldError}>{pwErrors.confirm}</Text>
          )}
        </div>
        <Button
          appearance="primary"
          icon={<SaveRegular />}
          disabled={pwSubmitting}
          onClick={onChangePassword}
        >
          {pwSubmitting ? '提交中…' : '修改密码'}
        </Button>
      </Card>

      {/* 4. 危险区 */}
      <Card className={styles.card} appearance="outline">
        <CardHeader header={<Text className={styles.cardHeader}>退出登录</Text>} />
        <DangerButton icon={<SignOutRegular />} onClick={() => setLogoutOpen(true)}>
          退出登录
        </DangerButton>
      </Card>

      <Divider />
      <Text className={styles.footerText}>Ming-GIT — v0.2.0</Text>

      {/* 退出登录确认 */}
      <Dialog open={logoutOpen} onOpenChange={(_, d) => setLogoutOpen(d.open)} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认退出登录?</DialogTitle>
            <DialogContent>
              退出后需要重新输入用户名 / 密码登录
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">取消</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={doLogout}>
                退出
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
